/* eslint-disable react-refresh/only-export-components -- hook module: it
   returns ReactNodes (overlay/controls) but exports no components, so there
   is no fast-refresh boundary to preserve. */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, MenuItem, Select, Slider, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import MovieIcon from "@mui/icons-material/Movie";
import { useQueries } from "@tanstack/react-query";
import type * as GeoJSON from "geojson";
import type { GeoJSONSource, Map as MapboxMap } from "mapbox-gl";

import { SimAnimationMetric, StorySceneAnimation } from "../../types/storyMap";
import {
  storyAnimationQueryOptions,
  StoryAnimationData,
} from "../../api/storyMaps";
import { entryProjectId } from "./layerEntries";
import {
  boundsToCoordinates,
  renderConfidenceFrame,
  renderMetricFrame,
  FireView,
} from "../layer_components/fire/fireCanvasEngine";
import { ConfidenceArrays, FrameArrays, RasterBounds } from "../../types/FireSimulation";
import { useHandheldLayout } from "./useHandheldLayout";
import {
  StoryCanvasLayer,
  URBAN_STATE_FILL_PAINT,
  URBAN_STATE_LINE_PAINT,
  URBAN_EDGE_SOLID_FILTER,
  URBAN_EDGE_SOLID_PAINT,
  URBAN_EDGE_EMBER_FILTER,
  URBAN_EDGE_EMBER_PAINT,
  URBAN_EDGE_LINE_LAYOUT,
  URBAN_EDGE_ARROW_IMAGE_ID,
  URBAN_EDGE_ARROW_LAYOUT,
  URBAN_EDGE_ARROW_PAINT,
  buildUrbanArrowImage,
} from "./storyLayers";
import { urbanEdgeOpacity } from "../layer_components/urbanEdgeFade";

/** Story animation metric → fireCanvasEngine view (shared with print). */
export const METRIC_TO_VIEW: Record<
  SimAnimationMetric,
  Exclude<FireView, "confidence">
> = {
  ros: "rate_of_spread",
  intensity: "fireline_intensity",
  flame_height: "flame_height",
};

const SPEEDS = [0.5, 1, 2, 4];

/** Constant per-cell alpha for colour-tinted entries (~0.85). */
const TINT_ALPHA = 217;

/** Elapsed seconds since simulation start → "h:mm". */
function formatElapsed(seconds: number | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}

type RGB = [number, number, number];

function hexToRgb(hex: string): RGB {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

/**
 * Tinted render path (entry.color set): burned cells (arrival <= t) are the
 * flat entry colour at constant alpha, instead of the metric palette.
 * Implemented here rather than in fireCanvasEngine.ts — story-map specific.
 * Exported for the print page's final-frame capture (useFinalFrameLayers).
 */
export function renderTintedFrame(
  out: ImageData,
  fa: FrameArrays,
  frameIdx: number,
  color: string
): void {
  const [h, w] = fa.shape;
  const n = w * h;
  const data = out.data;
  const arrival = fa.arrival;
  const tSec = fa.frameTimesSeconds[frameIdx] ?? Number.POSITIVE_INFINITY;
  const [r, g, b] = hexToRgb(color);
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    if (arrival[i] <= tSec) {
      data[o] = r;
      data[o + 1] = g;
      data[o + 2] = b;
      data[o + 3] = TINT_ALPHA;
    } else {
      data[o + 3] = 0;
    }
  }
}

/**
 * Tinted Confidence render: flat entry colour with alpha still rising with
 * burn probability (faint = uncertain), mirroring renderConfidenceFrame's
 * alpha ramp. Exported for the print page's final-frame capture.
 */
export function renderTintedConfidence(
  out: ImageData,
  ca: ConfidenceArrays,
  frameIdx: number,
  color: string
): void {
  const [h, w] = ca.shape;
  const n = w * h;
  const data = out.data;
  const f = Math.max(0, Math.min(frameIdx, ca.frameCount - 1));
  const base = f * n;
  const bp = ca.burnProb;
  const [r, g, b] = hexToRgb(color);
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const p = bp[base + i] / 255;
    if (p > 0) {
      data[o] = r;
      data[o + 1] = g;
      data[o + 2] = b;
      data[o + 3] = Math.round((0.2 + 0.65 * p) * 255);
    } else {
      data[o + 3] = 0;
    }
  }
}

/**
 * Quadratic-bezier arc between two points (UrbanIgnitionEdgesLayer's shape),
 * so ignition-pathway edges read as curved "jumps" rather than straight lines.
 */
function quadraticArc(
  p1: [number, number],
  p2: [number, number],
  curvature = 0.35,
  steps = 24
): [number, number][] {
  const mx = (p1[0] + p2[0]) / 2;
  const my = (p1[1] + p2[1]) / 2;
  const dx = p2[0] - p1[0];
  const dy = p2[1] - p1[1];
  const cpx = mx - dy * curvature;
  const cpy = my + dx * curvature;
  const pts: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const s = i / steps;
    const u = 1 - s;
    pts.push([
      u * u * p1[0] + 2 * u * s * cpx + s * s * p2[0],
      u * u * p1[1] + 2 * u * s * cpy + s * s * p2[1],
    ]);
  }
  return pts;
}

// The SDF arrowhead icon, built once per session (shared across maps).
let arrowImageCache: ImageData | null | undefined;
function urbanArrowImage(): ImageData | null {
  if (arrowImageCache === undefined) arrowImageCache = buildUrbanArrowImage();
  return arrowImageCache;
}

function boundsToFeature(bounds: RasterBounds): GeoJSON.Feature {
  const { west, south, east, north } = bounds;
  return {
    type: "Feature",
    properties: {},
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [west, south],
          [east, south],
          [east, north],
          [west, north],
          [west, south],
        ],
      ],
    },
  };
}

/** Last frame index with frame_times_seconds[i] <= t, or -1 before the first. */
function lastFrameAt(times: number[], t: number): number {
  let idx = -1;
  for (let i = 0; i < times.length; i++) {
    if (times[i] <= t) idx = i;
    else break;
  }
  return idx;
}

// Stable identity for query data objects (memo keys across renders, since
// useQueries returns a fresh array each render).
let nextObjId = 1;
const objIds = new WeakMap<object, number>();
function objId(o: object | null | undefined): number {
  if (!o) return 0;
  let id = objIds.get(o);
  if (!id) {
    id = nextObjId++;
    objIds.set(o, id);
  }
  return id;
}

function dataBounds(data: StoryAnimationData): RasterBounds {
  return data.kind === "frames" ? data.frames.bounds : data.confidence.bounds;
}

function dataShape(data: StoryAnimationData): [number, number] {
  return data.kind === "frames" ? data.frames.shape : data.confidence.shape;
}

/** Per-entry offscreen canvas + last-drawn signature (skips redundant draws). */
interface EntryCanvas {
  canvas: HTMLCanvasElement;
  img: ImageData | null;
  lastData: StoryAnimationData | null;
  lastFrameIdx: number;
  lastMetric: SimAnimationMetric | null;
  lastColor: string | null;
}

interface UseSceneAnimationsOptions {
  animations: StorySceneAnimation[] | null | undefined;
  /** Project stories (authed): the project entries without a projectId cite. */
  defaultProjectId?: number | null;
  /** Public viewer: frames come from each entry's frozen.framesUrl. */
  isPublic: boolean;
  /** Master gate — false while the scene isn't in animation mode / active. */
  enabled: boolean;
  /** Prefix for the per-entry map layer ids (unique per canvas). */
  layerIdPrefix?: string;
  /**
   * Project asset vector tiles (building footprints). With this plus entries
   * whose frame data carries urban timings, buildings are painted burning/
   * burnt in step with the shared clock (urban fire spread).
   */
  assetsTilesUrl?: string | null;
  /**
   * Folder and organisation-wide stories: asset tiles per cited project, so
   * each entry's buildings come from its own project.
   */
  assetsTilesUrlByProject?: Record<string, string | null>;
  /**
   * Underlying mapbox map (StoryMapCanvasHandle.getMap) — needed to apply the
   * per-building `urbanState` feature-states. Without it the urban overlay is
   * skipped; the raster animation is unaffected.
   */
  getMap?: () => MapboxMap | null;
}

export interface SceneAnimationsState {
  /**
   * One canvas-anim layer per ready entry, in scene.animations order (first =
   * bottom) — append to the StoryMapCanvas layer stack.
   */
  layers: StoryCanvasLayer[];
  /** Fit fallback: union of the entries' geographic bounds. */
  fitFeature: GeoJSON.Feature | null;
  /** Floating playback controls (bottom-center overlay); null until playable. */
  controls: React.ReactNode;
  /** Status message overlay (loading/error/unconfigured); null when playing. */
  overlay: React.ReactNode;
  /**
   * Urban fire legend (bottom-left overlay) — building states and ignition-
   * pathway line symbology; null when the scene has no urban data.
   */
  legend: React.ReactNode;
  /** Playback position in seconds since simulation start (weather layer). */
  elapsedSeconds: number;
}

/**
 * Drives a scene's animation stack on the main story map: fetches each
 * entry's arrays (single partition / percentile member / confidence) via one
 * useQueries call, renders frames client-side onto one offscreen canvas per
 * entry (fireCanvasEngine, or the local tinted path when entry.color is set),
 * and supplies shared playback controls.
 *
 * One SHARED clock: global elapsed seconds `t`; every entry shows its last
 * frame with frame_times_seconds[i] <= t (nothing before its first frame).
 * Confidence entries animate too — burn probability accumulating over the
 * run — since their burnProb stack is per-frame.
 */
export function useSceneAnimations({
  animations,
  defaultProjectId = null,
  isPublic,
  enabled,
  layerIdPrefix = "story-scene-anim",
  assetsTilesUrl = null,
  assetsTilesUrlByProject,
  getMap,
}: UseSceneAnimationsOptions): SceneAnimationsState {
  const entries = useMemo(() => animations ?? [], [animations]);
  const getMapRef = useRef(getMap);
  getMapRef.current = getMap;
  // Phones: the map is a short panel whose bottom edge carries the Mapbox
  // wordmark and attribution, and a full-width control bar sits on top of
  // them. Lift the floating overlays clear of that strip.
  const isMobile = useHandheldLayout();
  const overlayBottom = isMobile ? 36 : 24;

  const queries = useQueries({
    queries: entries.map((entry) => {
      const frozenUrl = entry.frozen?.framesUrl ?? null;
      const publicUnavailable =
        isPublic && (!!entry.frozen?.stale || !frozenUrl);
      const entryPid = entryProjectId(entry, defaultProjectId);
      return storyAnimationQueryOptions({
        projectId: entryPid !== null ? String(entryPid) : undefined,
        simulationId: entry.simulationId,
        view: entry.view,
        partition: entry.partition,
        resolvedMember: entry.resolvedMember ?? null,
        percentile: entry.percentile ?? null,
        publicUrl: isPublic ? frozenUrl : null,
        enabled: enabled && !publicUnavailable,
      });
    }),
  });

  // Identity keys so memos/effects only recompute when something real changed.
  const dataKey = queries.map((q) => objId(q.data ?? null)).join("|");
  const entriesKey = entries
    .map(
      (e) =>
        `${e.id}:${e.simulationId}:${e.view}:${e.partition}:${e.resolvedMember ?? ""}`
    )
    .join("|");
  const entriesLayerKey = entries
    .map((e) => `${e.id}:${e.opacity}`)
    .join("|");
  // What the draw effect reads beyond entry identity: recolouring an overlay
  // or switching its metric has to repaint even though the data is unchanged.
  const entriesDrawKey = entries
    .map((e) => `${e.id}:${e.metric}:${e.color ?? ""}`)
    .join("|");

  const canvasesRef = useRef<Map<string, EntryCanvas>>(new Map());
  const ensureCanvas = (id: string): EntryCanvas | null => {
    if (typeof document === "undefined") return null;
    let ec = canvasesRef.current.get(id);
    if (!ec) {
      ec = {
        canvas: document.createElement("canvas"),
        img: null,
        lastData: null,
        lastFrameIdx: -2,
        lastMetric: null,
        lastColor: null,
      };
      canvasesRef.current.set(id, ec);
    }
    return ec;
  };

  // ----- shared clock -----
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);

  // Scrubber max = max last frame time across all entries (confidence frames
  // are timed too — burn probability accumulates over the run); the tick step
  // is the smallest average frame interval, so no entry skips frames.
  const { maxTime, tickSeconds } = useMemo(() => {
    let maxT = 0;
    let tick = Number.POSITIVE_INFINITY;
    queries.forEach((q) => {
      const data = q.data;
      if (!data) return;
      const times =
        data.kind === "frames"
          ? data.frames.frameTimesSeconds
          : data.confidence.frameTimesSeconds;
      if (!times?.length) return;
      maxT = Math.max(maxT, times[times.length - 1]);
      if (times.length > 1) {
        const avg = (times[times.length - 1] - times[0]) / (times.length - 1);
        if (avg > 0) tick = Math.min(tick, avg);
      }
    });
    return {
      maxTime: maxT,
      tickSeconds: Number.isFinite(tick) ? tick : Math.max(1, maxT / 100),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey]);

  // Reset playback when the configured entry set changes.
  useEffect(() => {
    setT(0);
    setPlaying(false);
  }, [entriesKey]);

  // Playback loop: advance the shared clock one tick per timeout.
  useEffect(() => {
    if (!playing || maxTime <= 0) return;
    const timer = setTimeout(
      () => setT((prev) => (prev + tickSeconds > maxTime ? 0 : prev + tickSeconds)),
      50 / speed
    );
    return () => clearTimeout(timer);
  }, [playing, t, speed, maxTime, tickSeconds]);

  // ----- draw every entry's current frame (skips unchanged canvases) -----
  // Keyed on the clock plus the identity strings for the loaded data and the
  // configured entries: those are what can change what gets drawn. Without a
  // dependency list this ran on every render of the builder and the reader —
  // cheap per entry thanks to the signature check below, but it also re-ran
  // the orphan-canvas sweep each time for no reason.
  useEffect(() => {
    entries.forEach((entry, i) => {
      const data = queries[i]?.data;
      if (!data) return;
      const ec = ensureCanvas(entry.id);
      if (!ec) return;
      const [shapeH, shapeW] = dataShape(data);
      if (ec.canvas.width !== shapeW || ec.canvas.height !== shapeH) {
        ec.canvas.width = shapeW;
        ec.canvas.height = shapeH;
        ec.img = null;
        ec.lastData = null;
      }
      const ctx = ec.canvas.getContext("2d");
      if (!ctx) return;
      if (!ec.img) ec.img = ctx.createImageData(shapeW, shapeH);

      // Every entry follows the shared clock: last frame at or before t.
      // Confidence payloads without frame times (very old freezes) fall back
      // to their final full-run frame.
      const frameIdx =
        data.kind === "confidence"
          ? data.confidence.frameTimesSeconds?.length
            ? lastFrameAt(data.confidence.frameTimesSeconds, t)
            : data.confidence.frameCount - 1
          : lastFrameAt(data.frames.frameTimesSeconds, t);
      if (
        ec.lastData === data &&
        ec.lastFrameIdx === frameIdx &&
        ec.lastMetric === entry.metric &&
        ec.lastColor === entry.color
      ) {
        return;
      }
      if (frameIdx < 0) {
        // Before this entry's first frame: render nothing.
        ec.img.data.fill(0);
      } else if (data.kind === "confidence") {
        if (entry.color) {
          renderTintedConfidence(ec.img, data.confidence, frameIdx, entry.color);
        } else {
          renderConfidenceFrame(ec.img, data.confidence, frameIdx);
        }
      } else if (entry.color) {
        renderTintedFrame(ec.img, data.frames, frameIdx, entry.color);
      } else {
        renderMetricFrame(
          ec.img,
          METRIC_TO_VIEW[entry.metric] ?? "rate_of_spread",
          data.frames,
          frameIdx
        );
      }
      ctx.putImageData(ec.img, 0, 0);
      ec.lastData = data;
      ec.lastFrameIdx = frameIdx;
      ec.lastMetric = entry.metric;
      ec.lastColor = entry.color;
    });
    // Drop canvases for removed entries.
    const ids = new Set(entries.map((e) => e.id));
    Array.from(canvasesRef.current.keys()).forEach((id) => {
      if (!ids.has(id)) canvasesRef.current.delete(id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, dataKey, entriesKey, entriesDrawKey]);

  // ----- urban fire spread: per-building ignition/burnout timings -----
  // Single-partition and percentile-member frame data carry the urban timing
  // maps (asset id → seconds since the run's first frame); confidence has no
  // single scenario, so it carries none. Merge across entries: the earliest
  // ignition of a building wins, together with that entry's burnout time.
  // Times are shifted onto the shared clock (which runs in frame-time
  // seconds) by each entry's first frame time.
  const urbanTimings = useMemo(() => {
    const ignitionAt = new Map<number, number>();
    const burnoutAt = new Map<number, number>();
    entries.forEach((_entry, i) => {
      const data = queries[i]?.data;
      if (!data || data.kind !== "frames") return;
      const urban = data.frames.urban;
      const ignS = urban?.urban_asset_ignition_s;
      if (!ignS) return;
      const t0 = data.frames.frameTimesSeconds[0] ?? 0;
      const burnS = urban?.urban_asset_burnout_s;
      for (const [assetId, s] of Object.entries(ignS)) {
        const id = Number(assetId);
        const at = s + t0;
        const prev = ignitionAt.get(id);
        if (prev !== undefined && prev <= at) continue;
        ignitionAt.set(id, at);
        const bo = burnS?.[assetId];
        if (bo !== undefined) burnoutAt.set(id, bo + t0);
        else burnoutAt.delete(id);
      }
    });
    return { ignitionAt, burnoutAt };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey, entriesKey]);

  // Building footprints come from each cited project's asset tiles: one
  // source per project among the entries (a project story has one).
  const urbanSources = useMemo(() => {
    const out: { id: string; url: string }[] = [];
    if (!assetsTilesUrlByProject) {
      if (assetsTilesUrl) out.push({ id: `${layerIdPrefix}-urban-buildings`, url: assetsTilesUrl });
      return out;
    }
    const seen = new Set<string>();
    entries.forEach((entry) => {
      const pid = entryProjectId(entry, defaultProjectId);
      const key = pid !== null ? String(pid) : "";
      const url = key ? assetsTilesUrlByProject[key] ?? null : assetsTilesUrl;
      if (!url || seen.has(key)) return;
      seen.add(key);
      out.push({ id: key ? `${layerIdPrefix}-urban-buildings-${key}` : `${layerIdPrefix}-urban-buildings`, url });
    });
    return out;
  }, [assetsTilesUrlByProject, assetsTilesUrl, entries, defaultProjectId, layerIdPrefix]);
  const urbanSourcesKey = urbanSources.map((s) => `${s.id}=${s.url}`).join("|");
  const showUrban = enabled && urbanSources.length > 0 && urbanTimings.ignitionAt.size > 0;

  // Paint building states for the current clock position. Mirrors the project
  // page's UrbanBuildingStateLayer: set every currently-ignited building's
  // state each tick, clear the ones no longer ignited (scrubbing backwards /
  // switching entries). Feature-state survives tile loads, so applying before
  // tiles arrive is fine; the only guard needed is the source existing.
  const paintedAssetIdsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    const map = getMapRef.current?.() ?? null;
    if (!map) return;
    const sources = urbanSources.filter((s) => !!map.getSource(s.id));
    if (sources.length === 0) return;
    const { ignitionAt, burnoutAt } = urbanTimings;
    const nextPainted = new Set<number>();
    if (showUrban) {
      for (const [id, ignAt] of ignitionAt) {
        if (t < ignAt) continue;
        nextPainted.add(id);
        const boAt = burnoutAt.get(id);
        const state = { urbanState: boAt !== undefined && t >= boAt ? "burnt" : "burning" };
        sources.forEach((s) => map.setFeatureState({ source: s.id, sourceLayer: "assets", id }, state));
      }
    }
    for (const prevId of paintedAssetIdsRef.current) {
      if (!nextPainted.has(prevId)) {
        sources.forEach((s) =>
          map.removeFeatureState({ source: s.id, sourceLayer: "assets", id: prevId }, "urbanState")
        );
      }
    }
    paintedAssetIdsRef.current = nextPainted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, urbanTimings, showUrban, urbanSourcesKey, dataKey]);

  // ----- urban ignition-pathway arcs (convection / radiation / ember) -----
  // Precompute every edge's arc feature once per data change, sorted by its
  // clock time; the per-tick effect below just moves a cutoff. Only the
  // node-positions format is supported — every compact-frame-arrays era sim
  // writes it (the centroid fallback in the project page exists for legacy
  // vector-tile sims, which can't be story animations at all).
  const urbanEdges = useMemo(() => {
    const features: GeoJSON.Feature[] = [];
    entries.forEach((_entry, i) => {
      const data = queries[i]?.data;
      if (!data || data.kind !== "frames") return;
      const urban = data.frames.urban;
      const edges = urban?.urban_ignition_edges;
      const nodePositions = urban?.urban_node_positions;
      if (!edges?.length || !nodePositions) return;
      const t0 = data.frames.frameTimesSeconds[0] ?? 0;
      for (const edge of edges) {
        // Legacy interface edges are intentionally not rendered (matches the
        // project page); "wildland" bushfire→home edges ARE.
        if (edge.kind === "encroach" || edge.kind === "exit") continue;
        const dstPos = nodePositions[edge.dst_node];
        if (!dstPos) continue;
        let coordinates: [number, number][];
        if (edge.kind === "wildland" && edge.src_pos) {
          // Sourceless bushfire→home arc from the nearest fireline cell;
          // gentler curvature since an ember edge can be long.
          coordinates = quadraticArc(edge.src_pos, dstPos, 0.2);
        } else {
          const srcPos =
            edge.src_node !== null ? nodePositions[edge.src_node] : null;
          if (srcPos) {
            coordinates = quadraticArc(srcPos, dstPos);
          } else {
            const offset = 0.00025;
            coordinates = quadraticArc(
              [dstPos[0] - offset, dstPos[1] + offset],
              dstPos,
              0.6
            );
          }
        }
        features.push({
          type: "Feature",
          properties: { mode: edge.mode, at: edge.t + t0 },
          geometry: { type: "LineString", coordinates },
        });
      }
    });
    features.sort(
      (a, b) => (a.properties?.at as number) - (b.properties?.at as number)
    );
    // One FeatureCollection object shared by the three edge layers' sources;
    // the tick effect mutates `features` in place and re-pushes it, so a
    // canvas reconcile re-sending the declared data is self-consistent.
    const fc: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: [],
    };
    return { all: features, fc };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey, entriesKey]);

  const edgeLayerIdBase = `${layerIdPrefix}-urban-edges`;
  const hasEdges = urbanEdges.all.length > 0;
  const showEdges = enabled && hasEdges;

  // Advance the arc cutoff with the shared clock. Edges only appear/disappear
  // when the count of arcs with `at <= t` changes, so most ticks skip the
  // (worker-serialised) setData entirely.
  useEffect(() => {
    const map = getMapRef.current?.() ?? null;
    if (!map) return;
    const { all, fc } = urbanEdges;
    let count = 0;
    if (showEdges) {
      // First index with at > t (binary search over the sorted arcs).
      let lo = 0;
      let hi = all.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if ((all[mid].properties?.at as number) <= t) lo = mid + 1;
        else hi = mid;
      }
      count = lo;
    }
    if (count === fc.features.length) return;
    fc.features = all.slice(0, count);
    ["solid", "ember", "arrow"].forEach((suffix) => {
      const source = map.getSource(`${edgeLayerIdBase}-${suffix}`) as
        | GeoJSONSource
        | undefined;
      source?.setData(fc);
    });
  }, [t, urbanEdges, showEdges, edgeLayerIdBase]);

  // ----- layers: one canvas-anim per ready entry, in array order -----
  const layers = useMemo<StoryCanvasLayer[]>(() => {
    if (!enabled) return [];
    const out: StoryCanvasLayer[] = [];
    entries.forEach((entry, i) => {
      const data = queries[i]?.data;
      if (!data) return;
      const ec = ensureCanvas(entry.id);
      if (!ec) return;
      const opacity = Math.min(1, Math.max(0.2, entry.opacity ?? 0.85));
      out.push({
        id: `${layerIdPrefix}-${entry.id}`,
        kind: "canvas-anim",
        canvas: ec.canvas,
        coordinates: boundsToCoordinates(dataBounds(data)),
        opacity,
        visible: true,
      });
    });
    // Urban structures on top of the fire rasters: building footprints from
    // the project asset tiles, coloured burning/burnt via feature-state by the
    // clock effect above (same mechanism as the project page).
    if (showUrban) {
      urbanSources.forEach((source) => {
        out.push({
          id: source.id,
          kind: "vector",
          url: source.url,
          sourceLayer: "assets",
          paint: URBAN_STATE_FILL_PAINT,
          linePaint: URBAN_STATE_LINE_PAINT,
          minzoom: 0,
          // Must match the backend asset-tile max zoom (PMTILES_MAX_ZOOM = 12)
          // so mapbox overzooms z12 instead of requesting missing deeper tiles.
          maxzoom: 12,
          tileSize: 512,
          promoteId: "__id",
          visible: true,
        });
      });
    }
    // Ignition-pathway arcs above the buildings (the tick effect drives their
    // shared FeatureCollection). Zoom-gated by their paints/minzoom, so they
    // only appear once the reader is close enough for arcs to be legible.
    if (showEdges) {
      const arrow = urbanArrowImage();
      out.push(
        {
          id: `${edgeLayerIdBase}-solid`,
          kind: "geojson-line",
          data: urbanEdges.fc,
          paint: URBAN_EDGE_SOLID_PAINT,
          layout: URBAN_EDGE_LINE_LAYOUT,
          filter: URBAN_EDGE_SOLID_FILTER,
          minzoom: 14,
          visible: true,
        },
        {
          id: `${edgeLayerIdBase}-ember`,
          kind: "geojson-line",
          data: urbanEdges.fc,
          paint: URBAN_EDGE_EMBER_PAINT,
          layout: URBAN_EDGE_LINE_LAYOUT,
          filter: URBAN_EDGE_EMBER_FILTER,
          minzoom: 14,
          visible: true,
        }
      );
      if (arrow) {
        out.push({
          id: `${edgeLayerIdBase}-arrow`,
          kind: "geojson-symbol",
          data: urbanEdges.fc,
          paint: URBAN_EDGE_ARROW_PAINT,
          layout: URBAN_EDGE_ARROW_LAYOUT,
          image: { id: URBAN_EDGE_ARROW_IMAGE_ID, data: arrow, sdf: true },
          minzoom: 15.5,
          visible: true,
        });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, dataKey, entriesLayerKey, layerIdPrefix, showUrban, urbanSourcesKey, showEdges, urbanEdges, edgeLayerIdBase]);

  // Age the arcs with the clock (urbanEdgeFade): the paint carries the
  // current clock as a literal, so it is re-applied only when the clock
  // crosses a 5-minute bucket — invisible against a 30 min → 2 h fade and
  // far cheaper than re-evaluating three data-driven paints every 50 ms.
  const fadeBucket = Math.floor(t / 300);
  useEffect(() => {
    const map = getMapRef.current?.() ?? null;
    if (!map || !showEdges) return;
    const ageS = ["-", fadeBucket * 300, ["get", "at"]];
    const apply = (suffix: string, prop: "line-opacity" | "icon-opacity", expr: unknown[]) => {
      const id = `${edgeLayerIdBase}-${suffix}`;
      if (map.getLayer(id)) map.setPaintProperty(id, prop, expr as any);
    };
    apply("solid", "line-opacity", urbanEdgeOpacity(14, 15.5, 0.9, ageS));
    apply("ember", "line-opacity", urbanEdgeOpacity(14, 15.5, 1.0, ageS));
    apply("arrow", "icon-opacity", urbanEdgeOpacity(15.5, 16.5, 1, ageS));
    // `layers` is in the deps so a rebuilt edge layer (fresh paint from the
    // spec) is aged again straight away rather than at the next bucket.
  }, [fadeBucket, showEdges, edgeLayerIdBase, layers]);

  // Union of the ready entries' bounds (fit fallback).
  const fitFeature = useMemo<GeoJSON.Feature | null>(() => {
    let west = Infinity;
    let south = Infinity;
    let east = -Infinity;
    let north = -Infinity;
    queries.forEach((q) => {
      if (!q.data) return;
      const b = dataBounds(q.data);
      west = Math.min(west, b.west);
      south = Math.min(south, b.south);
      east = Math.max(east, b.east);
      north = Math.max(north, b.north);
    });
    if (!Number.isFinite(west) || !Number.isFinite(south)) return null;
    return boundsToFeature({ west, south, east, north });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey]);

  const anyReady = queries.some((q) => !!q.data);
  const anyError = queries.some((q) => q.isError);

  // ----- status overlay (shown only while nothing is renderable) -----
  const statusText = (() => {
    if (!enabled) return null;
    if (entries.length === 0) {
      return isPublic ? null : "Add a simulation in Scene settings.";
    }
    if (anyReady) return null;
    if (isPublic) {
      const allUnavailable = entries.every(
        (e) => !e.frozen?.framesUrl || e.frozen?.stale
      );
      if (allUnavailable) return "Animation unavailable — republish to refresh.";
      if (anyError) return "Couldn't load the animation data.";
      return "Loading animation…";
    }
    if (entries.every((e) => !e.simulationId)) {
      return "Choose a simulation in Scene settings.";
    }
    if (
      entries.some(
        (e) => e.simulationId && e.view === "single" && e.partition == null
      )
    ) {
      return "Click an ignition marker on the map to choose the fire.";
    }
    if (anyError) return "Couldn't load the animation data.";
    if (
      entries.some(
        (e) =>
          e.simulationId && e.view === "percentile" && e.resolvedMember == null
      )
    ) {
      return "Resolving the percentile member…";
    }
    return "Loading animation…";
  })();

  const overlay = statusText ? (
    <Box
      sx={{
        position: "absolute",
        bottom: overlayBottom,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 1,
        borderRadius: 2,
        bgcolor: "rgba(15, 23, 42, 0.9)",
        color: "rgba(255,255,255,0.85)",
        pointerEvents: "none",
        zIndex: 3,
        maxWidth: "80%",
      }}
    >
      <MovieIcon fontSize="small" />
      <Typography variant="body2">{statusText}</Typography>
    </Box>
  ) : null;

  // ----- floating playback controls (bottom-center, shared clock) -----
  const shownT = Math.min(t, maxTime);
  const controls =
    enabled && anyReady && maxTime > 0 && !statusText ? (
      <Box
        sx={{
          position: "absolute",
          bottom: overlayBottom,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          bgcolor: "rgba(15, 23, 42, 0.9)",
          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          zIndex: 3,
          width: "min(520px, calc(100% - 24px))",
          pointerEvents: "auto",
        }}
      >
        <IconButton
          size="small"
          onClick={() => setPlaying((p) => !p)}
          sx={{ color: "#00d4ad" }}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>
        <Select
          size="small"
          value={speed}
          onChange={(e) => setSpeed(Number(e.target.value))}
          sx={{
            fontSize: 12,
            minWidth: 64,
            color: "white",
            "& .MuiSelect-select": { py: 0.25 },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.3)",
            },
            "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.7)" },
          }}
        >
          {SPEEDS.map((s) => (
            <MenuItem key={s} value={s}>
              {s}x
            </MenuItem>
          ))}
        </Select>
        <Slider
          size="small"
          min={0}
          max={maxTime}
          step={tickSeconds}
          value={shownT}
          onChange={(_, value) => {
            setPlaying(false);
            setT(value as number);
          }}
          sx={{
            mx: 1,
            flexGrow: 1,
            color: "#00d4ad",
            "& .MuiSlider-rail": { bgcolor: "rgba(255,255,255,0.3)" },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontFamily: "monospace",
            color: "rgba(255,255,255,0.85)",
            minWidth: 84,
            textAlign: "right",
          }}
        >
          +{formatElapsed(shownT)} / {formatElapsed(maxTime)}
        </Typography>
      </Box>
    ) : null;

  // ----- urban fire legend (bottom-left; only when urban data is on show) --
  const legendRow = (swatch: React.ReactNode, label: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {swatch}
      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)" }}>
        {label}
      </Typography>
    </Box>
  );
  const boxSwatch = (fill: string, border: string) => (
    <Box
      sx={{
        width: 12,
        height: 12,
        bgcolor: fill,
        border: `1px solid ${border}`,
        borderRadius: "2px",
        flexShrink: 0,
      }}
    />
  );
  const lineSwatch = (css: string) => (
    <Box
      sx={{ width: 20, height: "3px", background: css, borderRadius: "1px", flexShrink: 0 }}
    />
  );
  const legend =
    enabled && !statusText && anyReady && (showUrban || showEdges) ? (
      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: "rgba(15, 23, 42, 0.9)",
          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          zIndex: 3,
          pointerEvents: "none",
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "white", fontWeight: 600, lineHeight: 1.2 }}
        >
          Urban fire
        </Typography>
        {showUrban && legendRow(boxSwatch("#ff6b00", "#ff4500"), "Burning")}
        {showUrban && legendRow(boxSwatch("#1a1a1a", "#555555"), "Burnt")}
        {/* Radiant = solid, ember = dashed — the same symbology whether the
            heat/embers come from a neighbouring house or from the bushfire. */}
        {showEdges && legendRow(lineSwatch("#ff6b00"), "Convection")}
        {showEdges && legendRow(lineSwatch("#ffcc00"), "Radiation")}
        {showEdges &&
          legendRow(
            lineSwatch(
              "repeating-linear-gradient(90deg, #dc13ff 0, #dc13ff 4px, transparent 4px, transparent 7px)"
            ),
            "Ember"
          )}
      </Box>
    ) : null;

  return { layers, fitFeature, controls, overlay, legend, elapsedSeconds: shownT };
}
